import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MaskedLines, Reveal } from "../components/Reveal";
import PageHero from "../components/PageHero";
import { api } from "../lib/api";
import { ArrowUpRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { featuredPortfolio } from "../data/featuredPortfolio";
import { JsonLd } from "../components/SEO";

export function PortfolioIndex() {
const [items, setItems] = useState(featuredPortfolio);

useEffect(() => {
  api
    .get("/portfolio")
    .then((r) => {
      const apiItems = Array.isArray(r.data) ? r.data : [];

      const featuredSlugs = new Set(
        featuredPortfolio.map((item) => item.slug)
      );

      const remainingItems = apiItems.filter(
        (item) => !featuredSlugs.has(item.slug)
      );

      setItems([...featuredPortfolio, ...remainingItems]);
    })
    .catch(() => {
    });
}, []);

  return (
    <>
      <section className="relative pt-44 lg:pt-71.8 pb-46 lg:pb-20 px-6 lg:px-12 bg-brand-bg overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1920&q=60&auto=format&fit=crop"
            alt=""
            aria-hidden="true"
            loading="eager"
            fetchpriority="high"
            decoding="async"
            className="w-full h-full object-cover opacity-[0.18] mix-blend-luminosity"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(20,18,22,0.72) 0%, rgba(20,18,22,0.55) 45%, var(--bg) 100%)" }} />
        </div>
        <div className="absolute inset-0 grain opacity-25" />
        <div className="absolute -top-40 -right-40 w-[60vw] h-[60vw] rounded-full pointer-events-none"
             style={{ background: "radial-gradient(circle, rgba(102,76,221,0.34), transparent 60%)" }} />
        <div className="absolute -bottom-40 -left-40 w-[45vw] h-[45vw] rounded-full pointer-events-none"
             style={{ background: "radial-gradient(circle, rgba(17,245,186,0.10), transparent 65%)" }} />

        <div className="max-w-[1600px] mx-auto relative">
  <div className="max-w-4xl">
    <h1 className="eyebrow mb-6 flex items-center gap-3">
      <span className="w-8 h-px bg-brand-purple" />
      Our Work
    </h1>

    <p
      className="font-display text-[13vw] sm:text-[10vw] lg:text-[9.5vw] leading-[1.295] tracking-tighter uppercase text-white"
      data-testid="services-hero-title"
    >
      <MaskedLines
        lines={[
          <>REAL PROJECTS</>,
          <><span className="font-serif-accent text-brand-purple lowercase">REAL IMPACT.</span></>,
        ]}
        stagger={0.14}
        delay={0.25}
      />
    </p>

    <motion.p
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 0.85 }}
      className="mt-16 pb-12 max-w-4xl text-zinc-400 text-lg leading-relaxed"
    >
    Explore our portfolio of SEO, Google Ads, Meta Ads, web development, branding, UI/UX design, and digital marketing projects that helped businesses generate traffic, leads, and measurable growth.
    </motion.p>
  </div>
</div>
      </section>

      <section className="bg-brand-bg py-16 lg:py-24 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto space-y-16 lg:space-y-28">
          {items.map((p, i) => (
            <Reveal key={p.id} delay={0.05}>
              <Link to={`/portfolio/${p.slug}`} data-testid={`portfolio-item-${p.slug}`} className={`group grid lg:grid-cols-12 gap-8 lg:gap-12 items-center ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                <div className="lg:col-span-7 spotlight-card aspect-[4/3] overflow-hidden border border-zinc-900">
                  <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-[1200ms]" />
                </div>
                <div className="lg:col-span-5">
                  <p className="eyebrow mb-4">{p.category} · {p.year}</p>
                  <h3 className="font-display text-3xl lg:text-5xl tracking-tight uppercase leading-[0.95]">{p.title}</h3>
                  <p className="mt-6 text-zinc-400 leading-relaxed">{p.summary}</p>
                  <div className="mt-8 flex flex-wrap gap-3">
                    {p.services.map(s => (
                      <span key={s} className="text-xs uppercase tracking-widest text-zinc-300 border border-zinc-800 rounded-full px-4 py-2">{s}</span>
                    ))}
                  </div>
                  <span className="mt-8 inline-flex items-center gap-2 text-brand-mint link-underline text-sm uppercase tracking-[0.24em]">
                    View project <ArrowUpRight size={14}/>
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
          {items.length === 0 && (
            <p className="text-zinc-500">Loading projects…</p>
          )}
        </div>
      </section>
    </>
  );
}

export function PortfolioDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  useEffect(() => {
  const localProject = featuredPortfolio.find(
    (project) => project.slug === slug
  );

  if (localProject) {
    setP(localProject);
    return;
  }

  api
    .get(`/portfolio/${slug}`)
    .then((r) => setP(r.data))
    .catch(() => {});
}, [slug]);
  if (!p) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Loading…</div>;

  const projectSchema = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: p.title,
    description: p.summary,
    url: `https://dorado9.com/portfolio/${p.slug}`,
    image: p.cover_image || undefined,
    creator: { "@id": "https://dorado9.com/#organization" },
    keywords: Array.isArray(p.services) ? p.services.join(", ") : undefined,
  };

  return (
    <>
      <JsonLd id="portfolio-project" data={projectSchema} />
      <section className="relative pt-40 lg:pt-56 pb-16 px-6 lg:px-12 bg-brand-bg">
        <div className="max-w-[1600px] mx-auto">
          <Link to="/portfolio" className="eyebrow inline-flex items-center gap-2 mb-8 hover:text-zinc-100 transition-colors">← Back to work</Link>
          <p className="eyebrow mb-4">{p.category} · {p.year}</p>
          <h1 className="font-display text-[10vw] lg:text-[7vw] leading-[0.9] tracking-tighter uppercase max-w-6xl">
            <MaskedLines lines={[p.title]} />
          </h1>
        </div>
      </section>

      <section className="bg-brand-bg px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto">
          <Reveal>
            <div className="aspect-[16/9] overflow-hidden border border-zinc-900">
              <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-bg py-24 lg:py-32 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 space-y-8">
            <Reveal>
              <p className="eyebrow mb-2">Client</p>
              <p className="font-display text-2xl">{p.client}</p>
            </Reveal>
            <Reveal>
              <p className="eyebrow mb-2">Services</p>
              <div className="flex flex-wrap gap-2">
                {p.services.map(s => <span key={s} className="text-xs uppercase tracking-widest border border-zinc-800 rounded-full px-3 py-1.5">{s}</span>)}
              </div>
            </Reveal>
            <Reveal>
              <p className="eyebrow mb-2">Year</p>
              <p className="font-serif-accent text-4xl text-brand-mint">{p.year}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-8 space-y-10">
            <Reveal>
              <p className="eyebrow mb-4">Overview</p>
              <p className="text-zinc-300 text-lg leading-relaxed">{p.summary}</p>
            </Reveal>
            <Reveal>
              <p className="eyebrow mb-6">Impact</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {p.metrics.map((m, i) => (
                  <div key={i} className="border-t border-brand-mint pt-5">
                    <p className="font-display text-2xl lg:text-3xl">{m}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
